import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CatOptions } from "@/components/cats/CatOptions";
import { SavedCats } from "@/components/cats/SavedCats";
import { CatFace, catLook } from "@/components/yarn/Cat";
import { PageHeader } from "@/components/yarn/PageHeader";
import { YarnBall } from "@/components/yarn/YarnBall";
import { useAuth } from "@/context/AuthContext";
import { api, errorText } from "@/lib/api";

const MAX = 6;
const lookOf = (c) => (c ? { fur: c.fur, pattern: c.pattern, eyes: c.eyes, hat: c.hat } : null);

export default function CatClosetPage() {
  const { user, updateUser } = useAuth();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = (d) => {
    setData(d);
    updateUser({ cat: lookOf(d.cats.find((c) => c.id === d.active_cat)) });
  };

  useEffect(() => {
    api.get("/cats").then(({ data: d }) => {
      setData(d);
      const active = d.cats.find((c) => c.id === d.active_cat);
      if (active) {
        setDraft({ ...active });
        setEditing(active.id);
      } else {
        setDraft({ name: "Whiskers", ...catLook(user.id) });
      }
    }).catch((e) => toast.error(errorText(e)));
  }, [user.id]);

  const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const { data: d } = await fn();
      apply(d);
      if (d.saved_id) setEditing(d.saved_id);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const body = draft && { name: draft.name, fur: draft.fur, pattern: draft.pattern, eyes: draft.eyes, hat: draft.hat };
  const saveNew = () => run(() => api.post("/cats", body), `${draft.name} saved and ready to play!`);
  const saveChanges = () => run(() => api.put(`/cats/${editing}`, body), "Looking sharp. Changes saved.");
  const activate = (id) => run(() => api.post(`/cats/${id}/activate`), "Cat swapped! They'll join your next game.");
  const remove = (id) => {
    if (id === editing) setEditing(null);
    run(() => api.delete(`/cats/${id}`), "Cat removed.");
  };
  const startNew = () => {
    setEditing(null);
    setDraft({ name: "New cat", ...catLook(Math.random().toString(36)) });
  };

  if (!data || !draft) {
    return (
      <div className="flex-1 grid place-items-center"><YarnBall size={48} className="yarn-spin" /></div>
    );
  }

  const isActive = editing && editing === data.active_cat;
  return (
    <div className="flex-1" data-testid="cat-closet-page">
      <PageHeader title="Cat closet" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 grid lg:grid-cols-[360px_minmax(0,1fr)] gap-6">
        <aside className="space-y-5 lg:sticky lg:top-24 self-start">
          <div className="patch patch-stitched p-6 text-center bg-[var(--yarn-linen)]" data-testid="cat-preview">
            <div className="yarn-bob inline-block"><CatFace look={draft} size={170} /></div>
            <p className="font-display text-2xl font-semibold mt-2 truncate" data-testid="cat-preview-name">{draft.name || "Unnamed cat"}</p>
            <p className="text-xs text-[var(--yarn-muted)] mb-4">
              {editing ? (isActive ? "This cat joins your games" : "Saved cat") : "Not saved yet"}
            </p>
            <div className="grid gap-2">
              {editing && (
                <button className="btn-yarn h-12" disabled={busy} onClick={saveChanges} data-testid="save-cat-changes-button">Save changes</button>
              )}
              <button className={`${editing ? "btn-soft" : "btn-yarn"} h-12`} disabled={busy || data.cats.length >= MAX} onClick={saveNew} data-testid="save-new-cat-button">
                {data.cats.length >= MAX ? "Closet full (6 cats)" : "Save as a new cat"}
              </button>
              {editing && !isActive && (
                <button className="btn-soft h-12" disabled={busy} onClick={() => activate(editing)} data-testid="use-this-cat-button">Play as this cat</button>
              )}
            </div>
          </div>
          <SavedCats cats={data.cats} active={data.active_cat} editing={editing} max={MAX}
            onEdit={(c) => { setDraft({ ...c }); setEditing(c.id); }} onActivate={activate} onDelete={remove} onNew={startNew} />
        </aside>
        <section className="min-w-0">
          <CatOptions draft={draft} set={set} season={data.season} />
        </section>
      </main>
    </div>
  );
}
